import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUser } from '../contexts/UserContext';
import { ref, push, set, get } from 'firebase/database';
import { database } from '../firebase';
import { findSchoolIdByName } from '../utils/schoolUtils';

const NewEvent: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // Check if user is admin (placeholder - implement based on your admin logic)
  const isAdmin = false; // TODO: Implement proper admin check
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: '',
    website: '',
    private: false,
    registerRequired: false,
    eventType: 'physical',
    // Poll-specific fields
    pollOptions: ['', ''], // Start with 2 empty options
    pollDurationHours: '0',
    pollDurationMinutes: '5',
    pollDurationSeconds: '0'
  });

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Pre-populate host field with user's school name
  useEffect(() => {
    if (user?.schoolName) {
      setFormData(prev => ({
        ...prev,
        host: user.schoolName
      }));
    }
  }, [user?.schoolName]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      
      // Auto-set End Date to Start Date when Start Date is selected (only if End Date is empty)
      if (field === 'startDate' && typeof value === 'string' && value) {
        // Only auto-populate if the start date is a complete date (MM/DD/YYYY format)
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (dateRegex.test(value) && !prev.endDate) {
          updatedData.endDate = value;
        }
      }
      
      // Auto-set End Time to Start Time + 1 hour when Start Time is selected (only if End Time is empty)
      if (field === 'startTime' && typeof value === 'string' && value && !prev.endTime) {
        // Parse the time value (could be in HH:MM AM/PM format or HH:MM format)
        let timeValue = value.trim();
        let hours = 0;
        let minutes = 0;
        
        // Handle AM/PM format
        if (timeValue.includes('AM') || timeValue.includes('PM')) {
          const isPM = timeValue.includes('PM');
          const timePart = timeValue.replace(/\s*(AM|PM)/i, '').trim();
          const [hourStr, minuteStr] = timePart.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
          
          // Convert to 24-hour format
          if (isPM && hours !== 12) {
            hours += 12;
          } else if (!isPM && hours === 12) {
            hours = 0;
          }
        } else {
          // Handle 24-hour format
          const [hourStr, minuteStr] = timeValue.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
        }
        
        // Add 1 hour
        hours += 1;
        if (hours >= 24) {
          hours = 0; // Wrap to next day
        }
        
        // Format as HH:MM
        updatedData.endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // Auto-populate Physical Location with "Virtual" when Event Type is set to Virtual Event
      if (field === 'eventType' && value === 'virtual') {
        updatedData.location = 'Virtual';
      }
      
      return updatedData;
    });
  };

  // Handle poll option changes
  const handlePollOptionChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      pollOptions: prev.pollOptions.map((option, i) => i === index ? value : option)
    }));
  };

  // Add a new poll option
  const addPollOption = () => {
    if (formData.pollOptions.length < 4) {
      setFormData(prev => ({
        ...prev,
        pollOptions: [...prev.pollOptions, '']
      }));
    }
  };

  // Remove a poll option
  const removePollOption = (index: number) => {
    if (formData.pollOptions.length > 2) {
      setFormData(prev => ({
        ...prev,
        pollOptions: prev.pollOptions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.uid) {
      setSaveError('User not authenticated');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Format the data to match Firebase structure
      const eventContent: any = {
        title: formData.name,
        hostName: formData.host,
        description: formData.description,
        website: formData.website,
        eventType: formData.eventType,
        isPostToAll: '', // Default to empty string for new events
        hasTarget: false, // Default to false for new events
        eventTargetStr: '',
        activityID: '',
        meetingID: '',
        meetingPassword: ''
      };

      // Handle poll-specific fields
      if (formData.eventType === 'poll') {
        // Filter out empty poll options and create array of strings
        const validOptions = formData.pollOptions.filter(option => option.trim() !== '');
        if (validOptions.length < 2) {
          throw new Error('At least 2 poll options are required');
        }
        eventContent.pollOptions = validOptions;
        
        // Calculate poll duration in minutes
        const hours = parseInt(formData.pollDurationHours) || 0;
        const minutes = parseInt(formData.pollDurationMinutes) || 0;
        const seconds = parseInt(formData.pollDurationSeconds) || 0;
        const totalMinutes = hours * 60 + minutes + (seconds / 60);
        eventContent.pollDuration = totalMinutes;
        // Calculate poll end timestamp (current time + duration in milliseconds)
        const currentTime = Date.now();
        const durationMs = totalMinutes * 60 * 1000; // Convert minutes to milliseconds
        eventContent.pollEndTimestamp = currentTime + durationMs;
      } else {
        // Handle regular event fields
        eventContent.address = formData.location;
        eventContent.private = formData.private;
        eventContent.registerRequired = formData.registerRequired;

        // Add start and end times if they exist
        if (formData.startDate && formData.startTime) {
          const startDateTime = `${formData.startDate} ${formData.startTime}`;
          eventContent.startTime = startDateTime;
        }

        if (formData.endDate && formData.endTime) {
          const endDateTime = `${formData.endDate} ${formData.endTime}`;
          eventContent.endTime = endDateTime;
        }
      }

      const eventData = {
        eventContent,
        postTime: Date.now(),
      };

      // Get the host school ID from the host name
      if (!formData.host || formData.host.trim() === '') {
        throw new Error('Host name is required');
      }

      const schoolId = await findSchoolIdByName(formData.host);
      if (!schoolId) {
        throw new Error(`School not found: ${formData.host}`);
      }

      // Create the event in Firebase
      const eventsRef = ref(database, `schoolEvent/${schoolId}/${user.uid}`);
      const newEventRef = push(eventsRef);
      
      await set(newEventRef, eventData);

      // Navigate back to events list on success
      navigate('/events');
    } catch (error) {
      console.error('Error saving event:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save event');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, width: '100%' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mb: 2 }}>
        Back to Events
      </Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'left', mb: 3 }}>
          {formData.eventType === 'poll' ? 'New Poll' : 'New Event'}
        </Typography>

        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Host and Event Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Event Host"
                value={formData.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                disabled={!isAdmin}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.eventType}
                  label="Event Type"
                  onChange={(e) => handleInputChange('eventType', e.target.value)}
                >
                  <MenuItem value="physical">In-person Event</MenuItem>
                  <MenuItem value="virtual">Virtual Event</MenuItem>
                  <MenuItem value="news">News</MenuItem>
                  <MenuItem value="poll">Poll</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Event Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                placeholder={formData.eventType === 'poll' ? 'Poll Title*' : 'Event Title*'}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Event Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            {/* Website - shown for Event, News, and Poll */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website (optional)"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://example.com"
              />
            </Grid>

            {/* Poll-specific fields - only shown when Event Type is "Poll" */}
            {formData.eventType === 'poll' && (
              <>
                {/* Poll Options */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Poll Options
                  </Typography>
                  {formData.pollOptions.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label={`Option ${index + 1}`}
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => handlePollOptionChange(index, e.target.value)}
                        required
                      />
                      {formData.pollOptions.length > 2 && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => removePollOption(index)}
                          sx={{ minWidth: 'auto', px: 2 }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  ))}
                  {formData.pollOptions.length < 4 && (
                    <Button
                      variant="outlined"
                      onClick={addPollOption}
                      sx={{ mb: 2 }}
                    >
                      + Add Option
                    </Button>
                  )}
                </Grid>

                {/* Poll Duration */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Poll Duration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Hours"
                      type="number"
                      value={formData.pollDurationHours}
                      onChange={(e) => handleInputChange('pollDurationHours', e.target.value)}
                      InputProps={{ inputProps: { min: 0, max: 24 } }}
                      sx={{ flex: 1 }}
                      required
                    />
                    <TextField
                      label="Minutes"
                      type="number"
                      value={formData.pollDurationMinutes}
                      onChange={(e) => handleInputChange('pollDurationMinutes', e.target.value)}
                      InputProps={{ inputProps: { min: 0, max: 59 } }}
                      sx={{ flex: 1 }}
                      required
                    />
                    <TextField
                      label="Seconds"
                      type="number"
                      value={formData.pollDurationSeconds}
                      onChange={(e) => handleInputChange('pollDurationSeconds', e.target.value)}
                      InputProps={{ inputProps: { min: 0, max: 59 } }}
                      sx={{ flex: 1 }}
                      required
                    />
                  </Box>
                </Grid>
              </>
            )}

            {/* Physical Location - only shown when Event Type is "In-person Event" */}
            {formData.eventType === 'physical' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Physical Location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  required
                />
              </Grid>
            )}

            {/* Event-specific fields - only shown when Event Type is "In-person Event" or "Virtual Event" */}
            {(formData.eventType === 'physical' || formData.eventType === 'virtual') && (
              <>
                {/* Checkboxes */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.registerRequired} 
                          onChange={(e) => handleInputChange('registerRequired', e.target.checked)}
                        />
                      }
                      label="Registration Required"
                    />
                  </Box>
                </Grid>

                {/* Start Date and Time */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                {/* End Date and Time */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            {/* Note about post visibility */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'right' }}>
                All active users who attend your high school will receive this post.
              </Typography>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/events')}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
                >
                  {isSaving ? 'Creating...' : `Create ${formData.eventType === 'news' ? 'News' : formData.eventType === 'physical' ? 'In-person Event' : formData.eventType === 'virtual' ? 'Virtual Event' : formData.eventType === 'poll' ? 'Poll' : 'Event'}`}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default NewEvent; 